import { apiClient } from '@/lib/api-client';
import type { DetectedDevice } from './detect-device';
import { describeForRecord } from './detect-device';
import type { StepResult, TestKind } from './tests';

/** Plain words for a failed step, stored with it (the API keeps at most 200 characters). */
const STEP_ERRORS: Record<NonNullable<StepResult['error']>, string> = {
  memory: 'out of memory',
  failed: 'step failed',
};

/**
 * Sends one finished run to platform's `POST /v1/device-tests` (#489): who ran it, the active
 * organization and the time are the server's; the device facts and the step timings are these.
 * Never called without the user pressing «Invia al team».
 */
export async function submitDeviceTest(
  context: { readonly tenantId: string; readonly organizationId: string },
  kind: TestKind,
  device: DetectedDevice,
  model: string | undefined,
  steps: readonly StepResult[],
): Promise<boolean> {
  const described = describeForRecord(device, model);
  try {
    const { response } = await apiClient.POST('/v1/device-tests', {
      params: {
        header: {
          'X-Active-Tenant-Id': context.tenantId,
          'X-Active-Organization-Id': context.organizationId,
        },
      },
      body: {
        kind,
        appVersion: __APP_VERSION__.slice(0, 40),
        screen: device.screen,
        device: {
          ...described,
          ...(device.memoryGb !== undefined ? { memoryGb: device.memoryGb } : {}),
          ...(device.cores !== undefined ? { cores: device.cores } : {}),
        },
        steps: steps.slice(0, 50).map((step) => ({
          name: step.name,
          ms: step.ms,
          ok: step.ok,
          ...(step.figures ? { figures: { ...step.figures } } : {}),
          ...(step.error ? { error: STEP_ERRORS[step.error] } : {}),
        })),
      },
    });
    return response.ok;
  } catch {
    return false;
  }
}
